<template>
  <div class="createWorkPermit">
    <TMSHeader
      :title="$t('workPermit.title')"
      :buttons="buttons"
      :needGoback="true"
      :goBack="back"
      extendStickyClass="createWorkPermit_stickyHeader"
      :dotOption="dotOption"
    >
      <div
        slot="status"
        class="pointer work-permit-status"
        :class="statusStyle"
        @click="lifecycleModal"
      >
        <span>{{ status|customStatusLabel }}</span>
      </div>
      <i
        v-if="workerInfoLastUpdatedText"
        slot="status-detail"
        class="work-permit-last-updated"
      >
        {{ workerInfoLastUpdatedText }}
      </i>
    </TMSHeader>

    <Lifecycle
      ref="workPermitLifecycle"
      :modalId="`work-permit-modules-lifecycle-modal`"
      :commentAttr="lifecycleCommentAttrs.comments"
      :histories="histories || []"
      :type="'workPermit'"
    />
    <div class="createWorkPermit_allInfo">
      <ProjectInfo :projectPermit="parentPermit" />
      <TabsContainer
        :tabNames="tabNames"
        :class="['createWorkPermit_tabs', tabsHighLight]"
      >
        <GeneralInfo
          slot="tab-0"
          ref="step1_generalInfo"
          :isEdit="isProjectAvailable"
          :isProjectAvailable="isProjectAvailable"
        />
        <WorkerInfo
          slot="tab-1"
          ref="step2_workerInfo"
          :isEdit="isProjectAvailable"
        />
        <HoistingHotWork
          slot="tab-2"
          ref="step3_hoistingsInfo"
          :isEdit="isProjectAvailable"
          :isProjectAvailable="isProjectAvailable"
        />
      </TabsContainer>
    </div>
    <ActionConfirmModal
      ref="deleteWorkPermitModal"
      :modalId="`deleteWorkPermitModal_${workPermitId}`"
      :confirmTitle="isRejected ? deleteRejectedWPHint : $t('workPermit.deleteWPConfirmHint')"
    />
    <Modal
      ref="tooManyHoistingModal"
      :showCloseBtn="false"
    >
      <p class="tooManyHoistingModalMessage">
        {{ projectTooManyHoistingMessage }}
      </p>
      <div slot="btns">
        <button
          type="button"
          class="btn btn--orange"
          @click="closeTooManyHoistingModal"
        >
          {{ $t('action.close') }}
        </button>
      </div>
    </Modal>
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import ActionConfirmModal from '@/controls/ActionConfirmModal'
import TabsContainer from '@/controls/TabsContainer'
import TMSHeader from '@/controls/TMSHeader'
import Lifecycle from '@/modules/Lifecycle'
import Modal from '@/controls/Modal'
import { getBuildingFloorZoneMap } from '@/api/buildings/request'
import { getDraftHoistingCountMap } from '@/utils/workPermitUtils'
import { getMaxWAPPRHoistingNum, isHoistingExceedMaxNum } from '@/api/workPermit/request'
import { sendEmail } from '@/api/postman/request'
import * as workPermitUtils from '@/api/workPermit/util'
import { ProjectTabs } from '@/modules/projects/consts'
import { TenantProjectTabs } from '@/modules/tenantProjects/consts'
import { utils as formUtils } from '@/plugin/InputChange'
import mutationTypes from '@/store/mutation-types'
import navigation from '@/utils/navigation'
import { CONTRACTOR_TYPES } from '@/modules/tenantProjects/contractors/consts'
import { EmailTypes, lifecycleCommentAttrs } from '@/utils/consts'
import notification from '@/utils/notification'
import {
  CreateWorkPermitTabs,
  DEFAULT_MAX_WAPPR_HOISTING_NUM,
  WORK_PERMIT_STATUS,
  WORK_PERMIT_TYPES,
  WP_PROJECT_TYPE_MAP,
} from './consts'
import { formatWorkPermitHistoriesForViewer, getIsProjectAvailable, getHoistingExceedData, getWorkPermitHistoryRowStatus, disableTenantContractorEdit, getWorkerInfoLastUpdatedText, showHoistingByModuleNHoisting } from './utils'
import WorkerInfo from './WorkerInfo'
import ProjectInfo from './ProjectInfo'
import HoistingHotWork from './HoistingHotWork'
import GeneralInfo from './GeneralInfo'
import {
  getDeleteRejectedWPHint,
  getHoistingAndHotWorkLabel,
  getRegionalWorkPermitLabel,
  getTooManyHoistingMessage,
} from './regionalWorkPermitLabels'

export default {
  name: 'CreateWorkPermit',
  components: {
    ActionConfirmModal,
    GeneralInfo,
    HoistingHotWork,
    Lifecycle,
    Modal,
    ProjectInfo,
    TabsContainer,
    TMSHeader,
    WorkerInfo,
  },
  data () {
    return {
      routeFrom: '',
      workPermitId: '',
      isValidStep1: true,
      isValidStep2: true,
      isValidStep3: true,
      isProjectAvailable: false,
      lifecycleCommentAttrs,
      maxWAPPRHoistingNum: DEFAULT_MAX_WAPPR_HOISTING_NUM,
      showHoistingModule: true,
    }
  },
  computed: {
    ...mapGetters('workPermit', ['currentWorkPermit', 'parentPermit', 'workPermitsInProject']),
    ...mapGetters('projectContractors', ['isCurrentVendorAccount', 'isMainUser', 'myContractor']),
    ...mapGetters('regions', ['backEndRegionalEnv']),

    tabNames () {
      const hoistingAndHotWorkLabel = this.hoistingAndHotWorkLabel
      const defaultTab = [
        this.$t('workPermit.generalInfo'),
        this.$t('workPermit.workerInfo'),
        hoistingAndHotWorkLabel,
      ]
      const index = defaultTab.indexOf(hoistingAndHotWorkLabel)
      if (!this.showHoistingModule) {
        defaultTab.splice(index, 1)
      }
      return defaultTab
    },
    hoistingLabel () {
      return getRegionalWorkPermitLabel('hoisting')
    },
    hoistingAndHotWorkLabel () {
      return getHoistingAndHotWorkLabel()
    },
    deleteRejectedWPHint () {
      return getDeleteRejectedWPHint()
    },
    projectTooManyHoistingMessage () {
      return getTooManyHoistingMessage(
        this.maxWAPPRHoistingNum,
        this.$t('workPermit.HoistingLimitHintType.project'),
      )
    },
    tabsHighLight () {
      let highLightCls = ''
      if (!this.isValidStep1) {
        highLightCls += 'step1_highLight'
      }
      if (!this.isValidStep2) {
        highLightCls += ' step2_highLight'
      }
      if (!this.isValidStep3) {
        highLightCls += ' step3_highLight'
      }
      return highLightCls
    },
    status () {
      return lodash.get(this.currentWorkPermit, 'status', '')
    },
    workerInfoLastUpdatedText () {
      return getWorkerInfoLastUpdatedText(this.currentWorkPermit, this.$t.bind(this))
    },
    showBtns () {
      return !disableTenantContractorEdit() || this.currentWorkPermit.type === WORK_PERMIT_TYPES.PROJECT
    },
    statusStyle () {
      return this.status ? `workpermit-status-${this.status.toLowerCase()} workpermit-status float-left` : ''
    },
    buttons () {
      return (this.isProjectAvailable && this.showBtns) ? [{
        title: this.$t('action.save'),
        cls: 'btn--darkblue',
        func: this.save,
      }, {
        title: this.$t('action.submit'),
        cls: this.submitDisabled ? 'btn--grey unclickable' : 'btn--orange',
        func: this.submit,
      }] : []
    },
    submitDisabled () {
      const parentId = (this.currentWorkPermit.parentId || '').trim()
      return parentId && (lodash.isEmpty(this.parentPermit) || this.isParentWPSuspended)
    },
    isParentWPSuspended () {
      return lodash.get(this, 'parentPermit.status', '') === WORK_PERMIT_STATUS.SUSPENDED
    },
    isRejected () {
      const histories = lodash.get(this.currentWorkPermit, 'histories', [])
      return histories.some(
        h => getWorkPermitHistoryRowStatus(h) === WORK_PERMIT_STATUS.REJECTED,
      )
    },
    histories () {
      return formatWorkPermitHistoriesForViewer(this.currentWorkPermit.histories, {
        formatComments: false,
      })
    },
    dotOption () {
      return [WORK_PERMIT_STATUS.DRAFT, WORK_PERMIT_STATUS.REJECTED].includes(this.status) && this.showBtns ? [{
        title: this.$t('action.delete'),
        operate: this.openConfirmDeleteModal,
      }] : []
    },
  },
  async mounted () {
    this.workPermitId = this.$route.params.id
    this.routeFrom = this.$route.query.from
    const wp = await this.getWorkPermitById(this.workPermitId)
    if (![WORK_PERMIT_STATUS.DRAFT, WORK_PERMIT_STATUS.REJECTED].includes(wp.status)) {
      return
    }
    if (wp.isTenantProject && wp.type === WORK_PERMIT_TYPES.PROJECT && wp.project.type === WP_PROJECT_TYPE_MAP.Fitout) {
      this.updateFieldByName({
        name: 'floors',
        value: lodash.union(wp.project.floors, wp.floors),
      })
    }
    await this.getWorkPermitsInProjectByProjectId({ projectId: this.currentWorkPermit.projectId })
    const buildingsInfo = await getBuildingFloorZoneMap()
    this.setBuildingFloorMap(buildingsInfo.BuildingFloorZoneMap)
    this.isProjectAvailable = await this._isProjectAvailable(wp)
    this.showHoistingModule = showHoistingByModuleNHoisting(this.currentWorkPermit.hoistings)
    this.initHoistingPosition()
  },
  methods: {
    ...mapActions('workPermit', ['getWorkPermitById', 'updateWorkPermitById', 'getProjectPermit',
      'submitWorkPermit', 'deleteWorkPermit', 'getWorkPermitsInProjectByProjectId']),
    ...mapActions('tenantProject', ['loadTenantProject']),
    ...mapActions('projects', ['loadProject']),
    ...mapMutations('workPermit', {
      setBuildingFloorMap: mutationTypes.WORKPERMIT_SET_BUILDING_FLOOR_MAP,
      setWorkPermitStatus: mutationTypes.WORKPERMIT_SET_CURRENT_WORKPERMIT,
      updateFieldByName: mutationTypes.WORKPERMIT_SET_WORKPERMIT_FIELD_BY_NAME,
    }),
    back () {
      if (this.routeFrom) {
        const tabIndex = this.currentWorkPermit.isTenantProject ? TenantProjectTabs.workPermit : ProjectTabs.workPermit
        navigation.replacePage(this.routeFrom,
          { id: this.currentWorkPermit.projectId },
          { tabIndex })
      } else {
        this.$router.goBack()
      }
    },
    lifecycleModal () {
      this.$refs.workPermitLifecycle.init()
    },
    async submit () {
      if (!this.submitDisabled) {
        this.isValidStep1 = this.$refs['step1_generalInfo'].validate()
        this.isValidStep2 = this.$refs['step2_workerInfo'].validate()
        this.isValidStep3 = this.$refs['step3_hoistingsInfo'].validate()
        if (!this.isValidStep1 || !this.isValidStep2 || !this.isValidStep3) {
          const formTitleMaps = {
            [this.$t('workPermit.generalInfo')]: this.isValidStep1,
            [this.$t('workPermit.workerInfo')]: this.isValidStep2,
            [this.hoistingLabel]: this.isValidStep3,
          }
          const listFormatParams = Object.entries(formTitleMaps).map(([localeKey, isValid]) => !isValid && localeKey).filter(v => v)
          listFormatParams.length && notification.fireNotification(false, this.$t(`workPermit.emptyFieldMessage.${listFormatParams.length}_empty`, listFormatParams))
          return
        }

        this.maxWAPPRHoistingNum = await getMaxWAPPRHoistingNum()
        const draftHoistingCountMap = getDraftHoistingCountMap(this.currentWorkPermit)
        const contractorType = this.isCurrentVendorAccount ? CONTRACTOR_TYPES.MAIN : this.myContractor.type
        this.updateFieldByName({name: 'contractorType', value: contractorType})
        const buildingId = workPermitUtils.getAttrOfBuilding(this.currentWorkPermit, 'id')
        const hoistingInfo = getHoistingExceedData(this.currentWorkPermit, draftHoistingCountMap, contractorType, buildingId)
        const isHoistingCountValid = await isHoistingExceedMaxNum(hoistingInfo)

        if (!isHoistingCountValid) {
          this.openTooManyHoistingModal()
          return
        }

        const newWP = await this.submitWorkPermit()
        let emailType
        if (newWP.status === WORK_PERMIT_STATUS.WAPPR) {
          emailType = newWP.type === WORK_PERMIT_TYPES.PROJECT ? EmailTypes.workPermits.firstApplyWP : EmailTypes.workPermits.applyWP
          await sendEmail(emailType, newWP)
          await sendEmail(EmailTypes.workPermits.applyWpToContractor, newWP)
        }
        formUtils.resetDirty()
        workPermitUtils.navigate(newWP, '')
      }
    },
    async save () {
      await this.updateWorkPermitById()
      formUtils.resetDirty()
    },
    async _deleteWorkPermit () {
      await this.deleteWorkPermit(this.currentWorkPermit)
      formUtils.resetDirty()
      workPermitUtils.deletePPNavigate(this.currentWorkPermit)
    },
    openConfirmDeleteModal () {
      this.$refs.deleteWorkPermitModal.open(this._deleteWorkPermit)
    },
    async _isProjectAvailable (workpermit) {
      const isTenantProject = workpermit.isTenantProject
      const project = isTenantProject ? await this.loadTenantProject(workpermit.projectId) : await this.loadProject(workpermit.projectId)
      return getIsProjectAvailable({ isTenantProject, project })
    },
    initHoistingPosition () {
      const hoistingId = this.$route.query.hoistingId
      if (hoistingId && +this.$route.query.tabIndex === CreateWorkPermitTabs.hoistingHotWork) {
        this.$refs.step3_hoistingsInfo.scrollTo(hoistingId)
      }
    },
    openTooManyHoistingModal () {
      this.$refs.tooManyHoistingModal.open()
    },
    closeTooManyHoistingModal () {
      this.$refs.tooManyHoistingModal.close()
    },
  },
}
</script>

<style lang='scss'>
.createWorkPermit {
  background: #FAFBFC;
  .createWorkPermit_allInfo {
    max-width: 1440px;
    margin: 15px auto;
    background: white;
    padding: 20px 20px 0 20px;
    box-shadow: 0 2px 4px 0 rgba(212, 212, 212, 0.5);
  }
  .tms-header {
    padding-left: 15px;
    padding-right: 15px;
    .tms-header__box {
      position: relative;
      .title {
        font-size: 22px !important;
      }
      .button-grops {
        position: absolute;
        right: 0px;
        top: 30px;
      }
    }
    .createWorkPermit_stickyHeader {
      .button-grops {
        top: 0;
      }
      & > div {
        z-index: 101;
      }
      & ::v-deep .sticky-content {
        height: 0;
        overflow: visible;
      }
    }
  }
  .createWorkPermit_tabs {
    .tabs-title a {
      font-size: 16px;
      font-weight: bold;
    }
    .hkld-tabs-content .tabs-panel{
      background: white;
    }
  }
  .work-permit-last-updated {
    color: #878787;
    font-size: 12px;
    line-height: 20px;
    padding: 0 10px;
    white-space: nowrap;
  }
  .bold {
    font-weight: bold;
  }
  .step1_highLight {
    li:first-child a {
      background-color: #f9ecea !important;
    }
    li.is-active a {
      background-color: #0C3A58 !important;
    }
  }
  .step2_highLight {
    li:nth-child(2) a {
      background-color: #f9ecea !important;
    }
    li.is-active a {
      background-color: #0C3A58 !important;
    }
  }
  .step3_highLight {
    li:nth-child(3) a {
      background-color: #f9ecea !important;
    }
    li.is-active a {
      background-color: #0C3A58 !important;
    }
  }
}
</style>

<style scoped>
  .tooManyHoistingModalMessage {
    font-weight: bold;
  }
</style>
